import type { ReactNode } from "react";
import {
  isTextItem,
  isToolCallResultItem,
  isToolCallStartItem,
  subagentActivity,
  TASK_TOOL_NAME,
} from "./AiToolboxSection.stream";
import type { DisplayContentItem } from "./AiToolboxSection.types";
import SubagentIndicator from "./components/SubagentIndicator/SubagentIndicator";
import { SubagentStatus } from "./components/SubagentIndicator/SubagentIndicator.types";
import ToolCallIndicatorGroup from "./components/ToolCallIndicators/components/ToolCallIndicatorGroup/ToolCallIndicatorGroup";
import { LOAD_SKILL_TOOL_NAME } from "./components/ToolCallIndicators/ToolCallIndicator.constants";
import type { ToolCallNodeProps } from "./components/ToolCallIndicators/ToolCallIndicator.types";

export interface GroupDisplayContentOptions {
  defaultToolCallsExpanded?: boolean;
}

/** Converts a flat list of DisplayContentItems into grouped ReactNodes for rendering. */
export function groupDisplayContent(
  content: DisplayContentItem[],
  options?: GroupDisplayContentOptions
): ReactNode[] {
  const nodes: ReactNode[] = [];
  let i = 0;

  while (i < content.length) {
    const item = content[i];

    if (isTextItem(item)) {
      nodes.push(item.content);
      i += 1;
      continue;
    }

    if (item.name === TASK_TOOL_NAME) {
      const input = item.input as
        | { subagent_type?: string; description?: string }
        | undefined;
      const subagent = item.subagent;
      const isRunning = isToolCallStartItem(item);

      nodes.push(
        <SubagentIndicator
          key={item.toolCallId}
          subagentType={subagent?.subagentType ?? input?.subagent_type}
          description={subagent?.description ?? input?.description}
          status={isRunning ? SubagentStatus.Pending : SubagentStatus.Completed}
          activity={isRunning ? subagentActivity(subagent?.items ?? []) : undefined}
          isError={isToolCallResultItem(item) ? item.isError : undefined}
          data-test-id="subagent-indicator"
        >
          {subagent?.items.length ? groupDisplayContent(subagent.items) : null}
        </SubagentIndicator>
      );
      i += 1;
      continue;
    }

    if (isToolCallStartItem(item) || isToolCallResultItem(item)) {
      const isReasoning = item.name === LOAD_SKILL_TOOL_NAME;
      const groupItems: ToolCallNodeProps[] = [];
      const groupKey = item.toolCallId;

      while (i < content.length) {
        const cur = content[i];

        if (isTextItem(cur)) {
          break;
        }

        if (!isToolCallStartItem(cur) && !isToolCallResultItem(cur)) {
          break;
        }

        if (cur.name === TASK_TOOL_NAME) {
          break;
        }

        const curIsReasoning = cur.name === LOAD_SKILL_TOOL_NAME;

        if (curIsReasoning !== isReasoning) {
          break;
        }

        const common = {
          name: cur.name ?? "",
          toolCallId: cur.toolCallId,
          input: cur.input,
        };

        groupItems.push(
          isToolCallStartItem(cur)
            ? {
                status: "pending",
                ...common,
                hasInputParseError: cur.hasInputParseError,
              }
            : {
                status: "completed",
                ...common,
                body: cur.body,
                isError: cur.isError,
              }
        );
        i += 1;
      }

      if (groupItems.length > 0) {
        nodes.push(
          <ToolCallIndicatorGroup
            key={groupKey}
            kind={isReasoning ? "reasoning" : "sources"}
            items={groupItems}
            defaultExpanded={options?.defaultToolCallsExpanded}
          />
        );
      }
      continue;
    }

    i += 1;
  }

  return nodes;
}
